import { create } from 'zustand';
import { StatusComment } from '../types';
import apiService from '../services/api';
import { getErrorMessage } from '../utils/errors';
import { useUserStatusStore } from './userStatusStore';
import { useAuthStore } from './authStore';

export interface CommentThreadState {
  items: StatusComment[];
  nextCursor: string | null;
  hasMore: boolean;
  hasLoaded: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  editingIds: Set<number>;
  likingIds: Set<number>;
  error: string | null;
}

interface CommentStoreState {
  threads: Record<number, CommentThreadState>;
  liveCounts: Record<number, number>;
  fetchComments: (statusId: number) => Promise<void>;
  createComment: (statusId: number, message: string) => Promise<void>;
  updateComment: (statusId: number, commentId: number, message: string) => Promise<void>;
  likeComment: (statusId: number, commentId: number) => Promise<void>;
  receiveComment: (statusId: number, comment: StatusComment, commentsCount?: number) => void;
  receiveUpdatedComment: (statusId: number, comment: StatusComment) => void;
  receiveCommentLike: (statusId: number, commentId: number, userId: number, likesCount: number, isLiked: boolean) => void;
  clearError: (statusId: number) => void;
}

const emptyThread = (): CommentThreadState => ({
  items: [],
  nextCursor: null,
  hasMore: false,
  hasLoaded: false,
  isLoading: false,
  isSubmitting: false,
  editingIds: new Set(),
  likingIds: new Set(),
  error: null,
});

export const useCommentStore = create<CommentStoreState>((set, get) => ({
  threads: {},
  liveCounts: {},

  fetchComments: async (statusId) => {
    const current = get().threads[statusId] ?? emptyThread();
    if (current.isLoading || (current.hasLoaded && !current.hasMore)) return;
    set((state) => ({
      threads: { ...state.threads, [statusId]: { ...current, isLoading: true, error: null } },
    }));
    try {
      const publicLoader = (apiService as unknown as {
        getPublicStatusComments?: typeof apiService.getPublicStatusComments;
      }).getPublicStatusComments?.bind(apiService);
      const page = await (!useAuthStore.getState().isAuthenticated && publicLoader
        ? publicLoader
        : apiService.getStatusComments.bind(apiService))(
        statusId,
        current.hasLoaded ? current.nextCursor ?? undefined : undefined,
      );
      set((state) => {
        const latest = state.threads[statusId] ?? emptyThread();
        const seen = new Set(latest.items.map((item) => item.id));
        const older = page.items.filter((item) => !seen.has(item.id));
        return {
          threads: {
            ...state.threads,
            [statusId]: {
              ...latest,
              items: [...older, ...latest.items],
              nextCursor: page.nextCursor,
              hasMore: page.hasMore,
              hasLoaded: true,
              isLoading: false,
            },
          },
        };
      });
    } catch (error) {
      set((state) => ({
        threads: {
          ...state.threads,
          [statusId]: {
            ...(state.threads[statusId] ?? emptyThread()),
            isLoading: false,
            error: getErrorMessage(error, 'Failed to load replies'),
          },
        },
      }));
      throw error;
    }
  },

  createComment: async (statusId, message) => {
    const current = get().threads[statusId] ?? emptyThread();
    if (current.isSubmitting) return;
    set((state) => ({
      threads: { ...state.threads, [statusId]: { ...current, isSubmitting: true, error: null } },
    }));
    try {
      const comment = await apiService.createStatusComment(statusId, message);
      get().receiveComment(statusId, comment);
      set((state) => {
        const latest = state.threads[statusId] ?? emptyThread();
        return {
          threads: {
            ...state.threads,
            [statusId]: { ...latest, hasLoaded: true, isSubmitting: false },
          },
        };
      });
    } catch (error) {
      set((state) => ({
        threads: {
          ...state.threads,
          [statusId]: {
            ...(state.threads[statusId] ?? emptyThread()),
            isSubmitting: false,
            error: getErrorMessage(error, 'Failed to post reply'),
          },
        },
      }));
      throw error;
    }
  },

  receiveComment: (statusId, comment, commentsCount) => {
    const current = get().threads[statusId] ?? emptyThread();
    const alreadyReceived = current.items.some((item) => item.id === comment.id);

    if (!alreadyReceived && commentsCount === undefined) {
      useUserStatusStore.getState().incrementCommentsCount(statusId);
    } else if (commentsCount !== undefined) {
      useUserStatusStore.getState().setCommentsCount(statusId, commentsCount);
    }

    set((state) => {
      const latest = state.threads[statusId] ?? emptyThread();
      const items = latest.items.some((item) => item.id === comment.id)
        ? latest.items
        : [...latest.items, comment];
      const previousLiveCount = state.liveCounts[statusId];
      return {
        threads: {
          ...state.threads,
          [statusId]: { ...latest, items },
        },
        liveCounts: commentsCount === undefined
          ? state.liveCounts
          : {
              ...state.liveCounts,
              [statusId]: Math.max(previousLiveCount ?? 0, commentsCount),
            },
      };
    });
  },

  updateComment: async (statusId, commentId, message) => {
    const current = get().threads[statusId] ?? emptyThread();
    if (current.editingIds.has(commentId)) return;
    const editingIds = new Set(current.editingIds);
    editingIds.add(commentId);
    set((state) => ({
      threads: { ...state.threads, [statusId]: { ...current, editingIds, error: null } },
    }));
    try {
      const comment = await apiService.updateComment(commentId, message);
      get().receiveUpdatedComment(statusId, comment);
    } catch (error) {
      set((state) => ({
        threads: {
          ...state.threads,
          [statusId]: {
            ...(state.threads[statusId] ?? emptyThread()),
            error: getErrorMessage(error, 'Failed to edit reply'),
          },
        },
      }));
      throw error;
    } finally {
      set((state) => {
        const latest = state.threads[statusId] ?? emptyThread();
        const nextEditing = new Set(latest.editingIds);
        nextEditing.delete(commentId);
        return {
          threads: {
            ...state.threads,
            [statusId]: { ...latest, editingIds: nextEditing },
          },
        };
      });
    }
  },

  receiveUpdatedComment: (statusId, comment) => set((state) => {
    const current = state.threads[statusId];
    if (!current) return state;
    return {
      threads: {
        ...state.threads,
        [statusId]: {
          ...current,
          items: current.items.map((item) => item.id === comment.id
            ? {
                ...comment,
                likesCount: item.likesCount,
                isLikedByCurrentUser: item.isLikedByCurrentUser,
              }
            : item),
        },
      },
    };
  }),

  receiveCommentLike: (statusId, commentId, userId, likesCount, isLiked) => {
    const isCurrentUser = useAuthStore.getState().user?.id === userId;
    set((state) => {
      const current = state.threads[statusId];
      if (!current) return state;
      return {
        threads: {
          ...state.threads,
          [statusId]: {
            ...current,
            items: current.items.map((item) => item.id === commentId
              ? {
                  ...item,
                  likesCount,
                  isLikedByCurrentUser: isCurrentUser
                    ? isLiked
                    : item.isLikedByCurrentUser,
                }
              : item),
          },
        },
      };
    });
  },

  likeComment: async (statusId, commentId) => {
    const current = get().threads[statusId] ?? emptyThread();
    if (current.likingIds.has(commentId)) return;
    const likingIds = new Set(current.likingIds);
    likingIds.add(commentId);
    set((state) => ({ threads: { ...state.threads, [statusId]: { ...current, likingIds, error: null } } }));
    try {
      const reaction = await apiService.likeComment(commentId);
      set((state) => {
        const latest = state.threads[statusId] ?? emptyThread();
        const nextLiking = new Set(latest.likingIds);
        nextLiking.delete(commentId);
        return {
          threads: {
            ...state.threads,
            [statusId]: {
              ...latest,
              likingIds: nextLiking,
              items: latest.items.map((item) => item.id === commentId ? { ...item, ...reaction } : item),
            },
          },
        };
      });
    } catch (error) {
      set((state) => {
        const latest = state.threads[statusId] ?? emptyThread();
        const nextLiking = new Set(latest.likingIds);
        nextLiking.delete(commentId);
        return {
          threads: {
            ...state.threads,
            [statusId]: { ...latest, likingIds: nextLiking, error: getErrorMessage(error, 'Failed to like reply') },
          },
        };
      });
      throw error;
    }
  },

  clearError: (statusId) => set((state) => ({
    threads: {
      ...state.threads,
      [statusId]: { ...(state.threads[statusId] ?? emptyThread()), error: null },
    },
  })),
}));
